import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Analytics } from '@vercel/analytics/react';
import { enableMapSet } from 'immer';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { toast } from 'sonner';
import { initializeDatabase } from './db/initialize';
import { checkDataIntegrity, repairSessionMetadata } from './db/session-events';
import i18n from './i18n';
import { initCrossTabSync } from './lib/cross-tab-sync';
import { handleBackgroundError } from './lib/error-handler';
import {
  onServiceWorkerActivated,
  registerSessionSyncWithServiceWorker,
  requestBackgroundSyncRegistration,
} from './lib/pwa/background-sync';
import { captureError } from './lib/sentry';
import { initSentry } from './lib/sentry';
import { supabase } from './lib/supabase';
import { handleSupabaseSessionChange } from './lib/supabase-session-manager';
import { toastSuccess } from './lib/toast-helpers';
import NotFoundPage from './routes/$';
import { routeTree } from './routeTree.gen';
import { useAuthStore } from './stores';

import './i18n/types';
import './index.css';

// Enable Immer MapSet plugin for Map/Set support
enableMapSet();

// Suppress React DevTools warning in development (informational only)
if (import.meta.env.DEV) {
  const originalWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    const message = args[0];
    if (
      typeof message === 'string' &&
      message.includes('Download the React DevTools for a better development experience')
    ) {
      // Suppress React DevTools informational message
      return;
    }
    originalWarn.apply(console, args);
  };
}

// Initialize cross-tab synchronization before other bootstrap logic
initCrossTabSync();

// Initialize Sentry before anything else
initSentry();

// Create a new router instance
const router = createRouter({
  routeTree,
  scrollRestoration: true,
  getScrollRestorationKey: (location) => location.pathname,
  notFoundMode: 'root',
  defaultNotFoundComponent: NotFoundPage,
});

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Register service worker for PWA
async function registerServiceWorker() {
  try {
    const { registerSW } = await import('virtual:pwa-register');
    const updateServiceWorker = registerSW({
      immediate: true,
      onRegistered(registration) {
        // Check for updates every hour
        if (registration) {
          setInterval(
            () => {
              void registration.update();
            },
            60 * 60 * 1000,
          );
        }
      },
      onNeedRefresh() {
        toast(i18n.t('pwa.toasts.updateAvailable'), {
          action: {
            label: i18n.t('pwa.toasts.refresh'),
            onClick: () => {
              void updateServiceWorker(true);
            },
          },
        });
      },
      onOfflineReady() {
        toastSuccess(i18n.t('pwa.toasts.offlineReady'));
      },
      onRegisterError(error) {
        console.warn('Service worker registration error:', error);
      },
    });

    onServiceWorkerActivated(() => {
      void registerSessionSyncWithServiceWorker();
      void requestBackgroundSyncRegistration();
    });

    await registerSessionSyncWithServiceWorker();
    await requestBackgroundSyncRegistration();
  } catch {
    // Service worker registration failed, app still works
  }
}

// Initialize authentication
async function initializeAuth() {
  try {
    const { getCurrentUser, setUser, setLoading } = useAuthStore.getState();

    // Subscribe to auth state changes
    supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? undefined);
      setLoading(false);
      handleSupabaseSessionChange(session ?? undefined);
    });

    // Get initial session
    await getCurrentUser();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    handleSupabaseSessionChange(session ?? undefined);
  } catch (error) {
    console.error('Failed to initialize auth:', error);
    useAuthStore.getState().setLoading(false);
  }
}

/**
 * Check data integrity in background (non-blocking)
 * Automatically repairs issues if found and reports to Sentry
 */
async function checkDataIntegrityInBackground(): Promise<void> {
  try {
    const result = await checkDataIntegrity();

    if (result.sessionsWithMissingMetadata > 0 || result.sessionsWithCorruptedMetadata > 0) {
      // Report to Sentry
      const error = new Error(
        `Data integrity issues detected: ${result.sessionsWithMissingMetadata} sessions with missing metadata, ${result.sessionsWithCorruptedMetadata} sessions with corrupted metadata`,
      );
      captureError(error, {
        operation: 'dataIntegrityCheck',
        sessionsWithMissingMetadata: result.sessionsWithMissingMetadata,
        sessionsWithCorruptedMetadata: result.sessionsWithCorruptedMetadata,
        totalSessionsWithEvents: result.totalSessionsWithEvents,
        issues: result.issues,
      });

      // Log to console for debugging
      console.warn('[Data Integrity] Issues detected, attempting automatic repair:', {
        missingMetadata: result.sessionsWithMissingMetadata,
        corruptedMetadata: result.sessionsWithCorruptedMetadata,
        total: result.totalSessionsWithEvents,
        issues: result.issues,
      });

      // Automatically repair issues
      try {
        const repairResult = await repairSessionMetadata();

        if (repairResult.sessionsRepaired > 0) {
          console.log(
            `[Data Integrity] Automatically repaired ${repairResult.sessionsRepaired} sessions`,
          );

          // Report successful repair to Sentry
          captureError(
            new Error(
              `Data integrity auto-repair completed: ${repairResult.sessionsRepaired} sessions repaired`,
            ),
            {
              operation: 'dataIntegrityAutoRepair',
              sessionsRepaired: repairResult.sessionsRepaired,
              sessionsSkipped: repairResult.sessionsSkipped,
              totalSessionsWithEvents: repairResult.totalSessionsWithEvents,
              errors: repairResult.errors,
            },
          );
        }

        if (repairResult.errors.length > 0) {
          // Report repair errors to Sentry
          console.warn(`[Data Integrity] Some sessions failed to repair:`, repairResult.errors);
          captureError(
            new Error(
              `Data integrity auto-repair had errors: ${repairResult.errors.length} sessions failed`,
            ),
            {
              operation: 'dataIntegrityAutoRepairErrors',
              errors: repairResult.errors,
            },
          );
        }
      } catch (repairError) {
        // Report repair failure to Sentry but don't block app
        handleBackgroundError(repairError, { operation: 'dataIntegrityAutoRepair' }, 'error');
      }
    }
  } catch (error) {
    // Don't block app startup if integrity check fails
    handleBackgroundError(error, { operation: 'dataIntegrityCheck' }, 'warn');
  }
}

async function bootstrap(): Promise<void> {
  try {
    await initializeDatabase();
  } catch (error) {
    console.error('Failed to initialize database:', error);
    // Even if database initialization fails, continue boot so UI can show errors.
  }

  void initializeAuth();

  if ('serviceWorker' in navigator) {
    void registerServiceWorker();
  }

  // Check data integrity in background (non-blocking, after initial load)
  // Use requestIdleCallback if available, otherwise setTimeout
  if (typeof requestIdleCallback === 'undefined') {
    setTimeout(() => {
      void checkDataIntegrityInBackground();
    }, 2000); // Wait 2 seconds after app loads
  } else {
    requestIdleCallback(() => {
      void checkDataIntegrityInBackground();
    });
  }
}

// eslint-disable-next-line unicorn/prefer-top-level-await
void bootstrap();

// Expose test helpers on window object for E2E tests
if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
  const { createPlayer } = await import('./db/players');
  (globalThis as { __testHelpers?: { createPlayer?: typeof createPlayer } }).__testHelpers = {
    createPlayer,
  };
}

// Render the app
createRoot(document.querySelector('#root')!).render(
  <StrictMode>
    <RouterProvider router={router} />
    <Analytics debug={false} />
  </StrictMode>,
);
